import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AccountIdentifierModal
 *
 * Minimal "complete your account" modal — lets a user attach a phone to
 * an email-only account (or vice versa). Reuses the OTP UX from the
 * login screen: one input → 6-digit code → done. Required by inbound
 * email matching: a captured email needs to find a Putty account by
 * sender address, so users who signed up with phone need a way to add
 * an email and become reachable that way.
 *
 * On successful confirm we refresh the auth context so subsequent
 * useAuth().user reflects the newly attached identifier and the modal's
 * trigger condition (missing identifier) flips off.
 */
import React, { memo, useCallback, useState } from "react";
import { useAuth } from "@/auth/auth-context";
import { sendIdentifierOtp, confirmIdentifierOtp, } from "@/api/command-api";
import "./account-identifier-modal.scss";
function normalizePhone(input) {
    const digits = input.replace(/\D/g, "");
    if (digits.length === 10)
        return `+1${digits}`;
    if (!input.startsWith("+"))
        return `+${digits}`;
    return input;
}
export const AccountIdentifierModal = memo(({ type, onClose }) => {
    const { refreshUser } = useAuth();
    const [step, setStep] = useState("input");
    const [value, setValue] = useState("");
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState();
    const handleSend = useCallback(async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(undefined);
        try {
            const normalized = type === "phone" ? normalizePhone(value) : value.trim();
            await sendIdentifierOtp(type, normalized);
            setValue(normalized);
            setStep("code");
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Could not send code");
        }
        finally {
            setSubmitting(false);
        }
    }, [type, value]);
    const handleConfirm = useCallback(async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(undefined);
        try {
            await confirmIdentifierOtp(type, value, code);
            // Pull the updated session so JWT-derived user info reflects the
            // attached identifier — the trigger that opened this modal will
            // now stop rendering.
            await refreshUser();
            onClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Verification failed");
        }
        finally {
            setSubmitting(false);
        }
    }, [type, value, code, refreshUser, onClose]);
    const heading = type === "phone" ? "Add a phone number" : "Add an email address";
    return (_jsx("div", { className: "account-identifier-modal__backdrop", onClick: onClose, children: _jsxs("div", { className: "account-identifier-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: heading }), error ? _jsx("p", { className: "account-identifier-modal__error", children: error }) : null, step === "input" ? (_jsxs("form", { onSubmit: handleSend, children: [type === "phone" ? (_jsx("input", { type: "tel", autoComplete: "tel", value: value, onChange: (e) => setValue(e.target.value), placeholder: "555 123 4567", required: true, autoFocus: true })) : (_jsx("input", { type: "email", value: value, onChange: (e) => setValue(e.target.value), placeholder: "you@example.com", required: true, autoFocus: true })), _jsxs("div", { className: "account-identifier-modal__actions", children: [_jsx("button", { type: "button", onClick: onClose, disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", disabled: submitting, children: submitting ? "Sending…" : "Send code" })] })] })) : (_jsxs("form", { onSubmit: handleConfirm, children: [_jsx("input", { type: "text", inputMode: "numeric", autoComplete: "one-time-code", value: code, onChange: (e) => setCode(e.target.value), placeholder: "123456", required: true, autoFocus: true }), _jsxs("div", { className: "account-identifier-modal__actions", children: [_jsx("button", { type: "button", onClick: () => setStep("input"), disabled: submitting, children: "Back" }), _jsx("button", { type: "submit", disabled: submitting, children: submitting ? "Verifying…" : "Verify" })] })] }))] }) }));
});
